import { useState } from "react";
import { supabase } from "./supabase";

var C = {
  bg:"#0F1117", surface:"#1A1D27", surfaceHi:"#1E2230",
  border:"#2E3248", accent:"#F5A623", accentDim:"rgba(245,166,35,0.1)",
  blue:"#4A90D9", teal:"#38BFA1", success:"#4CAF6E",
  danger:"#E05C5C", text:"#E8EAF0", dim:"#9AA0C0", muted:"#6B7094",
};
var FM = { fontFamily:"'DM Mono','Courier New',monospace" };

var SLIDES = [
  {
    icon:"👋",
    color:C.accent,
    title:"Welcome to ChoreHabit",
    sub:"Your crew dashboard",
    body:"ChoreHabit helps you and your team stay on top of equipment maintenance. Your job is simple — log hours, flag issues, and keep things running. Let's walk through how it works.",
  },
  {
    icon:"📋",
    color:C.blue,
    title:"Logging Hours",
    sub:"The most important habit",
    body:"Every day you use a piece of equipment, log it. Tap the equipment card on your dashboard, enter the current hours or mileage, and hit Save. That's it. This keeps maintenance intervals accurate for the whole team.",
    tip:"Tip: Log at the end of your shift while it's fresh. Takes 30 seconds.",
  },
  {
    icon:"🟡",
    color:C.accent,
    title:"Status Lights",
    sub:"Green, yellow, red",
    body:"When you log hours you'll set a status on the equipment. Green means all good. Yellow means something needs attention soon — add a note about what you noticed. Red means it's down or unsafe to operate.",
    tip:"Yellow and red automatically create a service request that your admin sees immediately.",
  },
  {
    icon:"🚨",
    color:C.danger,
    title:"Service Requests",
    sub:"Flag issues in real time",
    body:"If something's wrong with a piece of equipment — unusual noise, fluid leak, won't start — submit a service request. Your admin sees it instantly with your note attached. Never bury a problem.",
    tip:"Tip: Be specific in your notes. 'Making noise' is less useful than 'grinding noise from left side when turning.'",
  },
  {
    icon:"✅",
    color:C.success,
    title:"Marking Maintenance Done",
    sub:"Close the loop",
    body:"When you complete a maintenance task — oil change, filter swap, greasing — you can mark it done in the app. This updates the interval clock so nobody double-services equipment or misses it.",
  },
  {
    icon:"🤙",
    color:C.teal,
    title:"You're Ready",
    sub:"Never fall behind",
    body:"That's everything you need to know. Your admin has already added your equipment. Start by logging today's hours on anything you used. If you ever need to come back to this tutorial, find it in your menu.",
  },
];

export default function CrewTutorialScreen(props) {
  var onComplete = props.onComplete || function(){};
  var onClose = props.onClose || null; // null = mandatory first run
  var requirePassword = props.requirePassword === true; // only true when explicitly passed

  var s1=useState(0); var slide=s1[0]; var setSlide=s1[1];
  var s8=useState(requirePassword); var needsPassword=s8[0]; var setNeedsPassword=s8[1];
  var s9=useState(""); var pw=s9[0]; var setPw=s9[1];
  var s10=useState(""); var pwConfirm=s10[0]; var setPwConfirm=s10[1];
  var s11=useState(null); var pwErr=s11[0]; var setPwErr=s11[1];
  var s12=useState(false); var pwSaving=s12[0]; var setPwSaving=s12[1];

  function savePassword() {
    if (pw.length < 8) { setPwErr("Password must be at least 8 characters"); return; }
    if (pw !== pwConfirm) { setPwErr("Passwords do not match"); return; }
    setPwSaving(true); setPwErr(null);
    supabase.auth.updateUser({ password: pw }).then(function(res) {
      setPwSaving(false);
      if (res.error) { setPwErr(res.error.message); return; }
      setNeedsPassword(false);
    });
  }
  var total = SLIDES.length;
  var current = SLIDES[slide];
  var isLast = slide === total - 1;

  var css = "@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap'); *{box-sizing:border-box;margin:0;padding:0;} ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-thumb{background:#2E3248;border-radius:2px;}";
  var inp = { background:"#13151F", border:"1px solid #2E3248", borderRadius:8, padding:"12px 14px", color:"#E8EAF0", fontSize:13, fontFamily:"'DM Mono','Courier New',monospace", outline:"none", width:"100%", boxSizing:"border-box" };

  // Show password setup screen first for invited crew
  if (needsPassword) {
    return (
      <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>
        <style>{css}</style>
        <div style={{ width:"100%", maxWidth:400 }}>
          <img src="/chorehabit-app-tile.svg" alt="ChoreHabit" style={{ width:64, height:64, borderRadius:16, marginBottom:24 }} />
          <div style={{ fontSize:11, color:C.accent, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>Welcome to ChoreHabit</div>
          <div style={{ fontSize:24, fontWeight:700, marginBottom:6 }}>Set your password</div>
          <div style={{ fontSize:13, color:C.dim, marginBottom:28, lineHeight:1.6 }}>Choose a password to secure your crew account. You will use this to sign in going forward.</div>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, marginBottom:6 }}>New Password</div>
            <input style={inp} type="password" placeholder="8+ characters" value={pw} onChange={function(e){ setPw(e.target.value); setPwErr(null); }} />
          </div>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10, color:C.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, marginBottom:6 }}>Confirm Password</div>
            <input style={inp} type="password" placeholder="Repeat password" value={pwConfirm} onChange={function(e){ setPwConfirm(e.target.value); setPwErr(null); }} />
          </div>
          {pwErr && <div style={{ fontSize:11, color:C.danger, marginBottom:12, ...FM }}>{pwErr}</div>}
          <button
            onClick={savePassword}
            disabled={pwSaving||!pw||!pwConfirm}
            style={{ width:"100%", background:pwSaving||!pw||!pwConfirm?C.surfaceHi:C.accent, border:"none", borderRadius:10, color:pwSaving||!pw||!pwConfirm?C.muted:"#0F1117", fontSize:13, ...FM, fontWeight:700, padding:15, cursor:pwSaving||!pw||!pwConfirm?"not-allowed":"pointer", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}
          >
            {pwSaving?"Setting password...":"Set Password & Continue →"}
          </button>
          <div style={{ fontSize:11, color:C.muted, textAlign:"center", lineHeight:1.6 }}>
            Next you will get a quick walkthrough of the app before seeing your dashboard.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...FM, background:C.bg, minHeight:"100vh", color:C.text, display:"flex", flexDirection:"column", alignItems:"center" }}>
      <style>{css}</style>
      <div style={{ width:"100%", maxWidth:480, display:"flex", flexDirection:"column", minHeight:"100vh" }}>

        {/* Header */}
        <div style={{ padding:"20px 20px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontSize:11, color:C.accent, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase" }}>
            Crew Tutorial
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:11, ...FM, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Skip
            </button>
          )}
        </div>

        {/* Progress dots */}
        <div style={{ display:"flex", gap:6, padding:"16px 20px 0", justifyContent:"center" }}>
          {SLIDES.map(function(_, i) {
            return (
              <div key={i} onClick={function(){ setSlide(i); }} style={{ width: i===slide?24:8, height:8, borderRadius:4, background:i===slide?C.accent:i<slide?C.teal:C.border, cursor:"pointer", transition:"all 0.3s" }} />
            );
          })}
        </div>

        {/* Slide content */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"40px 24px 120px", justifyContent:"center" }}>

          {/* Icon */}
          <div style={{ width:88, height:88, borderRadius:24, background:"rgba(255,255,255,0.04)", border:"1px solid "+C.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:44, marginBottom:32, boxShadow:"0 8px 32px rgba(0,0,0,0.3)" }}>
            {current.icon}
          </div>

          {/* Label */}
          <div style={{ fontSize:10, color:current.color, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:10 }}>
            {current.sub}
          </div>

          {/* Title */}
          <div style={{ fontSize:28, fontWeight:700, lineHeight:1.15, marginBottom:20, color:C.text }}>
            {current.title}
          </div>

          {/* Body */}
          <div style={{ fontSize:15, color:C.dim, lineHeight:1.8, marginBottom:current.tip?20:0 }}>
            {current.body}
          </div>

          {/* Tip */}
          {current.tip && (
            <div style={{ background:C.accentDim, border:"1px solid "+C.accent, borderRadius:12, padding:"14px 16px", fontSize:12, color:C.accent, lineHeight:1.7, marginTop:8 }}>
              {current.tip}
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div style={{ position:"fixed", bottom:0, width:"100%", maxWidth:480, background:C.bg, borderTop:"1px solid "+C.border, padding:"14px 20px", display:"flex", gap:12, boxSizing:"border-box" }}>
          {slide > 0 && (
            <button onClick={function(){ setSlide(slide-1); }} style={{ flex:1, background:"none", border:"1px solid "+C.border, borderRadius:10, color:C.muted, fontSize:12, ...FM, fontWeight:700, padding:14, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Back
            </button>
          )}
          <button
            onClick={function(){ isLast ? onComplete() : setSlide(slide+1); }}
            style={{ flex:2, background:isLast?C.success:C.accent, border:"none", borderRadius:10, color:"#0F1117", fontSize:12, ...FM, fontWeight:700, padding:14, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em", boxShadow:isLast?"0 6px 24px rgba(76,175,110,0.3)":"0 6px 24px rgba(245,166,35,0.25)" }}
          >
            {isLast?"Let's Go →":"Next"}
          </button>
        </div>

      </div>
    </div>
  );
}
